import React, { useCallback, useMemo, useState } from 'react';

import { format, getZonedDate } from '@eika-infrastruktur/date';
import { capitalizeName, formatAccountNumber } from '@eika-infrastruktur/format';
import { WarningMessage } from '@eika/attention-message';
import { Button } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { IconTypes } from '@eika/icon';

import { useConfig } from '../../defaultConfig';
import { useAccountNumber, useAccounts } from '../../fetchHooks/useAccount';
import type { PaymentItemDetails } from '../../generated-types';
import { useActorReceiver } from '../../hooks/useSetReceiver';
import useTranslation from '../../hooks/useTranslation';
import { updatePayment } from '../../services/paymentService';
import { useSigningContext } from '../../signingContext/signingContext';
import { FormattedFuturePayment } from '../../utils/mapper/futurepaymentsUtil';
import { StopPaymentButton } from '../shared/StopPaymentButton';
import { currencyFormatter } from '../shared/numberFormat';
import { getFutureState } from '../transaction/Payment/form/state';
import { isNoCoverage } from '../transaction/Payment/form/validate';
import TransactionActorAddressField from '../transaction/TransactionDetails/TransactionActorAddressField';
import TransactionAmount from '../transaction/TransactionDetails/TransactionAmount';
import TransactionLine from '../transaction/TransactionDetails/TransactionLine';
import FuturePaymentButtonWrapper from './FuturePaymentButtonWrapper';
import FuturePaymentSigningWrapper from './FuturePaymentSigningWrapper';
import FuturePaymentWarningMessage from './FuturePaymentWarningMessage';
import composeApprovedByString from './utils/composeApprovedByString';
import mapFuturePaymentToRequest from './utils/mapFuturePaymentToRequest';

import './FuturePaymentDetailsView.scss';

type Props = {
    futurePaymentDetails: { data: PaymentItemDetails | undefined; isLoading: boolean };
    futurePayment: FormattedFuturePayment;
    editModalState: [boolean, React.Dispatch<boolean>];
    paymentButtonState: [boolean, React.Dispatch<boolean>];
    onClose: () => void;
};

function FuturePaymentDetailsView({
    futurePayment,
    futurePaymentDetails,
    editModalState: [openEditModal, setOpenEditModal],
    paymentButtonState: [hidePaymentButton, setHidePaymentButton],
    onClose,
}: Props) {
    const translated = useTranslation();
    const { featureToggles } = useConfig();

    const [isDeleting, setIsDeleting] = useState(false);

    const [errorMessage, setErrorMessage] = useState<string>();
    const { data: accounts = [] } = useAccounts();

    const toAccount = useActorReceiver(futurePayment.toAccount, futurePayment.paymentType === 'FOREIGN_PAYMENT');
    const { data: account } = useAccountNumber(futurePayment.fromAccount.accountNumber);
    const { canEdit, canReject } = useMemo(() => {
        const { canReject, canEdit: editAllowed } = futurePayment.permissions;
        const canEdit = editAllowed;
        return { canEdit, canReject };
    }, [futurePayment]);

    const isTransferType = useMemo(
        () => futurePayment.paymentType === 'TRANSFER' || futurePayment.paymentType === 'RECURRING_TRANSFER',
        [futurePayment],
    );

    const payNow = useCallback(() => {
        return updatePayment(
            { value: futurePayment.id },
            mapFuturePaymentToRequest({ ...futurePayment, date: getZonedDate() }, futurePaymentDetails.data),
        );
    }, [futurePayment, futurePaymentDetails.data]);

    const { signingOperationPending, startSigning } = useSigningContext(payNow);

    const onPayNow = useCallback(async () => {
        const hasNoMoneyToPayNow = isNoCoverage(
            {
                ...getFutureState(futurePayment),
                toAccount: futurePaymentDetails.data?.receiver.accountNumber
                    ? {
                          accountNumber: futurePaymentDetails.data?.receiver.accountNumber,
                          accountName: futurePaymentDetails.data?.receiver.name,
                      }
                    : undefined,
                fromAccount: futurePaymentDetails.data?.from.accountNumber,
                paymentDate: getZonedDate(),
            },
            accounts,
        );
        setErrorMessage(hasNoMoneyToPayNow ? translated.paymentValidation.noCoverage : undefined);
        if (!futurePaymentDetails.isLoading && !hasNoMoneyToPayNow) {
            setIsDeleting(false);
            startSigning();
        }
    }, [
        futurePaymentDetails,
        accounts,
        startSigning,
        futurePayment,
        translated.paymentValidation.noCoverage,
        setErrorMessage,
    ]);

    const approvers = useMemo(() => {
        const currentApprovers = futurePaymentDetails.data?.approvers ?? [];

        if (currentApprovers.length === 0) return undefined;
        const restOfApprovers = currentApprovers.filter((x) => !x.firstApprover);
        const firstApprover = currentApprovers.find((x) => x.firstApprover);
        return { firstApprover, restOfApprovers };
    }, [futurePaymentDetails]);

    return (
        <FuturePaymentSigningWrapper
            onClose={onClose}
            status={isDeleting ? null : 'payNow'}
            fromAccountId={account?.accountId}
            paymentType={futurePayment.paymentType}
            paymentDate={getZonedDate(futurePayment.date)}
            active={!openEditModal}
        >
            <div className="future-payment__details-wrapper">
                <TransactionAmount
                    value={futurePayment.estimatedAmount ?? futurePayment.amount}
                    typeLabel={
                        futurePayment.paymentType !== 'UNKNOWN' && futurePayment.paymentType
                            ? translated.futurePaymentType[futurePayment.paymentType]
                            : undefined
                    }
                    eFakturaId={futurePaymentDetails.data?.eFakturaId}
                    eFakturaLoading={futurePayment.paymentType === 'EFAKTURA' ? futurePaymentDetails.isLoading : false}
                    additionalInfo={
                        futurePayment.paymentType === 'FOREIGN_PAYMENT'
                            ? translated.futurePaymentDetails.calculatedAmount
                            : undefined
                    }
                    eFakturaLinkClassName="future-payment__details-eFaktura-link"
                />
                <div className="future-payment__details">
                    <TransactionLine
                        label={translated.from}
                        value={futurePayment.fromAccount.alias}
                        customValue={
                            futurePayment.fromAccount.accountNumber && (
                                <span className="transaction-details__payer-account-number">
                                    {formatAccountNumber(futurePayment.fromAccount.accountNumber)}
                                </span>
                            )
                        }
                    />
                    <TransactionLine
                        label={translated.to}
                        value={toAccount?.accountName}
                        customValue={
                            <span className="transaction-details__payer-account-number">
                                {toAccount?.accountNumber && formatAccountNumber(toAccount.accountNumber)}
                            </span>
                        }
                        isLoading={!toAccount}
                    />
                    <TransactionLine
                        value={futurePaymentDetails.data?.kid?.value}
                        label={translated.transactionDetails.kid}
                    />
                    <TransactionLine
                        value={futurePayment.date}
                        label={translated.date}
                        formatter={(date) => format(date, 'dd.MM.yyyy')}
                    />

                    <TransactionLine
                        show={futurePayment.paymentType === 'FOREIGN_PAYMENT'}
                        label={translated.futurePaymentDetails.amountInCurrency}
                        value={futurePayment.amount}
                        formatter={(value) => currencyFormatter(value, futurePayment.currency)}
                    />
                    <TransactionLine value={futurePayment.description} label={translated.transactionDetails.message} />
                    {futurePaymentDetails?.data?.note !== futurePaymentDetails?.data?.ownText && (
                        <TransactionLine
                            value={futurePaymentDetails.data?.ownText}
                            label={translated.transactionDetails.ownMessage}
                            isLoading={futurePaymentDetails.isLoading}
                        />
                    )}

                    <TransactionActorAddressField
                        label={translated.transactionDetails.receiver.address}
                        address={futurePaymentDetails.data?.receiver?.address}
                        isLoading={futurePaymentDetails.isLoading}
                    />
                    <TransactionLine
                        value={capitalizeName(approvers?.firstApprover?.name ?? futurePayment.owner?.name)}
                        label={translated.futurePaymentDetails.paidBy}
                    />
                    {approvers?.restOfApprovers && (
                        <TransactionLine
                            label={translated.transactionDetails.approvers.approvedBy}
                            value={composeApprovedByString(approvers.restOfApprovers)}
                        />
                    )}
                </div>
                {errorMessage && (
                    <WarningMessage
                        className="future-payment__details--error-message"
                        icon={IconTypes.UTROPSTEGN_24}
                        text={errorMessage}
                    />
                )}
            </div>
            <FuturePaymentButtonWrapper>
                <FuturePaymentButtonWrapper.FirstRow>
                    {canEdit && !hidePaymentButton && (
                        <Button
                            buttonType="negative"
                            onClick={() => {
                                setErrorMessage(undefined);
                                setOpenEditModal(true);
                            }}
                            data-testid="change-future-payment-button"
                        >
                            {translated.futurePaymentDetails.change}
                        </Button>
                    )}
                    {canEdit && featureToggles.enablePayNow && !hidePaymentButton && (
                        <ButtonWithSpinner
                            buttonType="positive"
                            showSpinner={signingOperationPending}
                            onClick={onPayNow}
                            data-testid="pay-now-future-payment-button"
                        >
                            {isTransferType ? translated.transferNow : translated.payNow}
                        </ButtonWithSpinner>
                    )}
                </FuturePaymentButtonWrapper.FirstRow>
                {canReject && (
                    <StopPaymentButton
                        paymentId={futurePayment.id}
                        paymentType={futurePayment.paymentType}
                        onExpandDialog={setHidePaymentButton}
                        setStatusToStopping={() => setIsDeleting(true)}
                    />
                )}
                {!canEdit && (
                    <FuturePaymentWarningMessage
                        paymentType={futurePayment.paymentType}
                        canReject={canReject}
                        isFaked={futurePayment.isFaked}
                    />
                )}
            </FuturePaymentButtonWrapper>
        </FuturePaymentSigningWrapper>
    );
}

export default FuturePaymentDetailsView;
